import dayjs from '../../utils/localDayjs'
import React, {useState, useEffect, useMemo} from 'react'
import {Calendar, DateObject} from 'react-multi-date-picker'
import Icon from '../icon'
import Popup from '../popup'
import {useTranslation} from '../../utils/useTranslation'
import './styles.css'
import {useCurrentShift, useSelectedDate, useViewMode} from '../../store'
import {getRailwayWeek} from '../../utils/dates'
import {useUIStore as useUIRaw} from '../../store/ui.slice'
import {theme} from '../../theme'
import {formatNightShiftDate} from '../../utils/formatNightShiftDate'
import {getDateFormatted} from '../../utils/getDateFormatted'

interface CalendarHeaderProps {
  /** Lock the navigation unit regardless of the global view mode. */
  forceViewMode?: 'week' | 'day'
}

const CalendarHeader = ({forceViewMode}: CalendarHeaderProps) => {
  const {t} = useTranslation()
  const setSelectedDate = useUIRaw(state => state.setSelectedDate)
  const setWeekNavigation = useUIRaw(state => state.setWeekNavigation)
  const setDayNavigation = useUIRaw(state => state.setDayNavigation)
  const selectedDate = useSelectedDate()
  const currentShift = useCurrentShift()
  const globalViewMode = useViewMode()
  const viewMode = forceViewMode ?? globalViewMode

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const displayedDate = dayjs(selectedDate)

  // Track which month the calendar popup is currently showing.
  // Resets to displayedDate each time the popup opens.
  const [calendarDisplayDate, setCalendarDisplayDate] = useState(displayedDate)
  useEffect(() => {
    if (anchorPosition) setCalendarDisplayDate(displayedDate)
  }, [anchorPosition])

  // Compute the railway week numbers for each row shown in the calendar popup.
  // The calendar always shows the weeks that cover the full displayed month,
  // starting from the Monday of the week containing the 1st.
  const calendarWeekNumbers = useMemo(() => {
    const firstOfMonth = calendarDisplayDate.startOf('month')
    const lastOfMonth = calendarDisplayDate.endOf('month')
    const firstMonday = firstOfMonth.subtract(firstOfMonth.weekday() - 1, 'day')
    const weeks: number[] = []
    let current = firstMonday
    do {
      weeks.push(getRailwayWeek(current))
      current = current.add(7, 'day')
    } while (!current.isAfter(lastOfMonth))
    return weeks
  }, [calendarDisplayDate])

  const commitDate = (newDate: dayjs.Dayjs) => {
    setSelectedDate(newDate.format('YYYY-MM-DD'))
  }

  const onClickTrigger = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setAnchorPosition({top: rect.y + rect.height, left: rect.x})
  }

  const handleCalendarChange = (val: DateObject) => {
    commitDate(dayjs(val.toDate()))
    setAnchorPosition(null)
  }

  const dateKey = displayedDate.format('YYYY-MM-DD')

  // Memoize the DateObject so its reference is stable across re-renders that
  // don't change the selected date (e.g. month navigation updates calendarDisplayDate).
  // Without this, react-multi-date-picker sees a new `value` object on every render
  // and resets the calendar's displayed month back to the selected date's month.
  const calendarValue = useMemo(
    () => new DateObject(displayedDate.toDate()),
    [dateKey],
  )

  // The value the trigger displays, self-labelled so its unit (week vs day) is
  // legible without relying on the neighbouring view-mode toggle. The year is
  // shown, de-emphasised, only when it differs from the current calendar year.
  const triggerLabel =
    viewMode === 'week'
      ? `${t('general:week')} ${getRailwayWeek(displayedDate)}`
      : getDateFormatted(displayedDate.toString(), currentShift, 'full')
  const showYear = displayedDate.year() !== dayjs().year()

  /**
   * Renders each calendar cell as "D-1 → D" to represent a night shift.
   * The stored value remains D (YYYY-MM-DD) — only the visual label changes.
   */
  const nightMapDays = ({date}: {date: DateObject}) => {
    return {
      children: (
        <span className='night-cell'>
          {formatNightShiftDate(dayjs(date.toDate()), 'minimal')}
        </span>
      ),
    }
  }

  return (
    <div className='calendarNavigator'>
      <div
        className='calendarNavArrow calendarNavArrowPrev'
        onClick={() =>
          viewMode === 'week'
            ? setWeekNavigation('prev')
            : setDayNavigation('prev')
        }>
        <Icon
          type='left_arrow'
          size='24px'
          color={theme.palette.text.primary}
        />
      </div>
      <div
        className={`calendarNavigatorTrigger${anchorPosition ? ' active' : ''}`}
        onClick={onClickTrigger}>
        <span className='calendarNavigatorValue'>{triggerLabel}</span>
        {showYear && (
          <span className='calendarNavigatorYear'>
            · {displayedDate.year()}
          </span>
        )}
        <Icon
          type='calendarMonth'
          size='24px'
          color={
            anchorPosition
              ? theme.palette.primary.main
              : theme.palette.text.primary
          }
        />
      </div>
      <Popup
        anchorPosition={anchorPosition}
        isShowPadding={false}
        isChangedLeft={true}
        backgroundColor={
          currentShift === 'night' ? theme.palette.shift.night.light : undefined
        }
        setAnchorPosition={setAnchorPosition}>
        <div
          className={`calendarHeaderWrapper${currentShift === 'night' ? ' night' : ''}`}>
          <div className='calendar-with-weeks'>
            {/* Custom railway week number column, aligned with calendar day rows */}
            <div className='railway-weeks-col'>
              <div className='railway-week-header' />
              {calendarWeekNumbers.map((wn, i) => (
                <div key={i} className='railway-week-cell'>
                  {wn}
                </div>
              ))}
            </div>
            <Calendar
              value={calendarValue}
              onChange={handleCalendarChange}
              onMonthChange={(date: DateObject) =>
                setCalendarDisplayDate(dayjs(date.toDate()))
              }
              weekStartDayIndex={1}
              showOtherDays
              weekDays={
                currentShift === 'night'
                  ? [
                      'Sa/Di',
                      'Di/Lu',
                      'Lu/Ma',
                      'Ma/Me',
                      'Me/Je',
                      'Je/Ve',
                      'Ve/Sa',
                    ]
                  : undefined
              }
              mapDays={currentShift === 'night' ? nightMapDays : undefined}
            />
          </div>
        </div>
      </Popup>
      <div
        className='calendarNavArrow calendarNavArrowNext'
        onClick={() =>
          viewMode === 'week'
            ? setWeekNavigation('next')
            : setDayNavigation('next')
        }>
        <Icon
          type='right_arrow'
          size='24px'
          color={theme.palette.text.primary}
        />
      </div>
    </div>
  )
}

export default CalendarHeader
